<template>
  <div class="coupon-edit-container">
    <el-card shadow="never" class="main-card">
      <div slot="header" class="card-header">
        <i class="el-icon-tickets title-icon"></i>
        <span>创建卡券</span>
      </div>

      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px" style="padding:0 20px;">
        <div class="section-title">基本信息</div>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="套餐名称" prop="name">
              <el-input v-model="form.name" placeholder="请输入套餐名称" maxlength="100" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="套餐标签" prop="tags">
              <div class="tag-selector">
                <el-tag
                    v-for="tag in tagOptions"
                    :key="tag.value"
                    :type="form.tags.includes(tag.value) ? tag.type : 'info'"
                    :effect="form.tags.includes(tag.value) ? 'dark' : 'plain'"
                    size="medium"
                    class="tag-item"
                    @click="toggleTag(tag.value)"
                >
                  {{ tag.label }}
                </el-tag>
              </div>
              <div v-if="form.tags.length > 0" class="selected-tags-info">
                已选择：{{ form.tags.join('、') }}
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-title">价格与库存</div>
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="售价(元)" prop="price">
              <el-input-number v-model="form.price" :min="0" :precision="2" :step="0.01" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="原价(元)" prop="originalPrice">
              <el-input-number v-model="form.originalPrice" :min="0" :precision="2" :step="0.01" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="库存" prop="stock">
              <el-input-number v-model="form.stock" :min="0" :max="999999" style="width:100%;" />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-title">券设置</div>
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="有效期(天)">
              <el-input-number v-model="form.voucherValidDays" :min="1" :max="365" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="使用次数">
              <el-input-number v-model="form.useLimit" :min="1" :max="99" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="上架状态">
              <el-radio-group v-model="form.status">
                <el-radio-button :label="1">上架</el-radio-button>
                <el-radio-button :label="0">下架</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-title">套餐描述</div>
        <el-form-item label="描述文字">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入套餐描述文字" maxlength="500" show-word-limit />
        </el-form-item>

        <el-form-item label="描述图片">
          <div class="desc-images-upload">
            <div class="images-list">
              <div
                  v-for="(img, index) in descImages"
                  :key="index"
                  class="image-item"
              >
                <img :src="img.url" class="image-thumb" />
                <div class="image-actions">
                  <i class="el-icon-zoom-in" @click="previewImage(img.url)"></i>
                  <i class="el-icon-delete" @click="removeDescImage(index)"></i>
                </div>
              </div>
              <div
                  v-if="descImages.length < 5"
                  class="upload-trigger"
                  @click="openDescImageSelector"
              >
                <i class="el-icon-plus"></i>
                <span>上传图片</span>
              </div>
            </div>
            <div class="upload-tip">最多上传5张图片，支持jpg/png格式</div>
          </div>
        </el-form-item>

        <div class="section-title">套餐封面</div>
        <el-form-item label="套餐主图">
          <div class="upload-row">
            <div class="upload-box" @click="openMainImageSelector">
              <template v-if="mainImageUrl">
                <img :src="mainImageUrl" class="upload-thumb" />
                <div class="upload-mask">
                  <i class="el-icon-delete" @click.stop="removeMainImage"></i>
                  <span style="color:#fff;margin-left:4px;">删除</span>
                </div>
              </template>
              <template v-else>
                <i class="el-icon-plus upload-icon"></i>
                <span>上传主图</span>
              </template>
            </div>
          </div>
        </el-form-item>

        <div class="section-title">包含商品</div>
        <el-form-item label="已选商品">
          <div class="selected-products">
            <el-tag
                v-for="(p, index) in form.products"
                :key="index"
                closable
                size="small"
                type="success"
                @close="removeProduct(index)"
                style="margin:4px;"
            >
              {{ productCache[p.productId] || '商品' + p.productId }} ×{{ p.productCount }}
            </el-tag>
          </div>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openProductDialog" style="margin-top:8px;">添加商品</el-button>
        </el-form-item>

        <el-form-item style="margin-top:40px;text-align:center;">
          <el-button @click="handleCancel">返回列表</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting" style="margin-left:16px;">立即创建</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 描述图片选择器 -->
    <image-selector
        v-model="showDescImageSelector"
        :max-count="5 - descImages.length"
        @select="handleDescImageSelect"
    />

    <!-- 主图选择器 -->
    <image-selector
        v-model="showMainImageSelector"
        :max-count="1"
        @select="handleMainImageSelect"
    />

    <!-- 选择商品弹窗 -->
    <el-dialog title="选择商品" :visible.sync="showProductDialog" width="900px" append-to-body>
      <div class="product-select-dialog">
        <el-input
            v-model="productSearch"
            placeholder="搜索商品名称"
            prefix-icon="el-icon-search"
            size="small"
            clearable
            style="margin-bottom:16px;"
            @input="searchProducts"
        />

        <el-table
            :data="dialogProductList"
            size="small"
            max-height="400"
            @selection-change="onProductSelectionChange"
            ref="productTable"
            v-loading="productLoading"
        >
          <el-table-column type="selection" width="50" />
          <el-table-column prop="name" label="商品名称" min-width="150" show-overflow-tooltip />
          <el-table-column label="售价" width="100">
            <template slot-scope="{row}">¥{{ formatPrice(row.price) }}</template>
          </el-table-column>
          <el-table-column label="库存" width="80" align="center">
            <template slot-scope="{row}">{{ row.stock || 0 }}</template>
          </el-table-column>
          <el-table-column label="份数" width="120">
            <template slot-scope="{row}">
              <el-input-number
                  v-model="productCountMap[row.id]"
                  :min="1"
                  :max="99"
                  size="mini"
                  controls-position="right"
                  style="width:100%;"
              />
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
            style="margin-top:16px;text-align:right;"
            :current-page="productPage"
            :page-size="10"
            :total="productTotal"
            layout="total, prev, pager, next"
            @current-change="loadProductPage"
            small
        />
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showProductDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmProductSelect">确定添加</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览弹窗 -->
    <el-dialog :visible.sync="showImagePreview" width="600px" append-to-body>
      <img :src="previewImageUrl" style="width:100%;" />
    </el-dialog>
  </div>
</template>

<script>
import api from "@/utils/api"
import ImageSelector from '@/components/ImageSelector'

export default {
  name: 'CouponAdd',
  components: { ImageSelector },
  data() {
    return {
      submitting: false,

      // 主图相关
      showMainImageSelector: false,
      mainImageUrl: '',

      // 描述图片相关
      showDescImageSelector: false,
      descImages: [],

      // 商品选择相关
      showProductDialog: false,
      productSearch: '',
      selectedProducts: [],
      productLoading: false,
      dialogProductList: [],
      productPage: 1,
      productTotal: 0,
      productCache: {},
      productCountMap: {},

      // 图片预览
      showImagePreview: false,
      previewImageUrl: '',

      // 标签选项
      tagOptions: [
        { label: '爆款', value: '爆款', type: 'danger' },
        { label: '新品', value: '新品', type: 'success' },
        { label: '聚会首选', value: '聚会首选', type: 'warning' },
        { label: '超值套餐', value: '超值套餐', type: 'primary' },
        { label: '限时特惠', value: '限时特惠', type: 'danger' },
        { label: '热卖', value: '热卖', type: '' },
        { label: '推荐', value: '推荐', type: 'success' },
        { label: '经典', value: '经典', type: 'info' }
      ],

      form: {
        name: '',
        tags: [],
        price: 0,
        originalPrice: 0,
        stock: 0,
        voucherValidDays: 30,
        useLimit: 1,
        status: 0,
        description: '',
        imageId: null,
        products: []
      },
      rules: {
        name: [{ required: true, message: '请输入套餐名称', trigger: 'blur' }],
        tags: [{
          type: 'array',
          required: true,
          message: '请至少选择一个套餐标签',
          trigger: 'change'
        }],
        price: [{ required: true, message: '请输入售价', trigger: 'blur' }],
        originalPrice: [{ required: true, message: '请输入原价', trigger: 'blur' }],
        stock: [{ required: true, message: '请输入库存', trigger: 'blur' }]
      }
    }
  },
  methods: {
    toggleTag(tagValue) {
      const index = this.form.tags.indexOf(tagValue)
      if (index > -1) {
        this.form.tags.splice(index, 1)
      } else {
        this.form.tags.push(tagValue)
      }
    },

    openDescImageSelector() {
      if (this.descImages.length >= 5) {
        this.$message.warning('最多上传5张图片')
        return
      }
      this.showDescImageSelector = true
    },

    handleDescImageSelect(images) {
      if (images && images.length) {
        images.forEach(img => {
          if (this.descImages.length < 5) {
            this.descImages.push({
              id: img.id,
              url: img.url
            })
          }
        })
      }
      this.showDescImageSelector = false
    },

    removeDescImage(index) {
      this.descImages.splice(index, 1)
    },

    openMainImageSelector() {
      this.showMainImageSelector = true
    },

    handleMainImageSelect(images) {
      if (images && images.length) {
        this.form.imageId = images[0].id
        this.mainImageUrl = images[0].url
      }
      this.showMainImageSelector = false
    },

    removeMainImage() {
      this.form.imageId = null
      this.mainImageUrl = ''
    },

    previewImage(url) {
      this.previewImageUrl = url
      this.showImagePreview = true
    },

    async openProductDialog() {
      this.showProductDialog = true
      this.productSearch = ''
      this.productPage = 1
      this.selectedProducts = []
      await this.loadProductPage(1)
    },

    async loadProductPage(page) {
      this.productPage = page
      this.productLoading = true
      try {
        const res = await api.product_getProductList({
          page,
          pageSize: 10,
          keyword: this.productSearch || undefined
        }, null)
        const d = res.data || res
        if (d.code === '000') {
          let list = d.data.list || d.data.records || d.data || []
          list = list.filter(p => p.status === 1)
          this.dialogProductList = list
          this.productTotal = d.data.totalCount || d.data.total || 0

          list.forEach(p => {
            this.$set(this.productCache, p.id, p.name)
            if (!this.productCountMap[p.id]) {
              this.$set(this.productCountMap, p.id, 1)
            }
          })
        }
      } catch (e) {
        this.$message.error('加载商品列表失败')
      } finally {
        this.productLoading = false
      }
    },

    searchProducts() {
      this.productPage = 1
      this.loadProductPage(1)
    },

    formatPrice(p) {
      return p ? (p / 100).toFixed(2) : '0.00'
    },

    removeProduct(index) {
      const product = this.form.products[index]
      if (product) {
        this.$delete(this.productCountMap, product.productId)
      }
      this.form.products.splice(index, 1)
    },

    onProductSelectionChange(val) {
      this.selectedProducts = val
    },

    confirmProductSelect() {
      if (this.selectedProducts.length === 0) {
        this.$message.warning('请先选择商品')
        return
      }

      this.selectedProducts.forEach(product => {
        this.$set(this.productCache, product.id, product.name)
        const count = this.productCountMap[product.id] || 1

        const existIndex = this.form.products.findIndex(item => item.productId === product.id)
        if (existIndex > -1) {
          this.$set(this.form.products, existIndex, {
            productId: product.id,
            productCount: count
          })
        } else {
          this.form.products.push({
            productId: product.id,
            productCount: count
          })
        }
      })

      this.showProductDialog = false
      this.selectedProducts = []
      this.$message.success('商品添加成功')
    },

    async handleSubmit() {
      try {
        await this.$refs.formRef.validate()
      } catch (e) {
        return
      }

      if (!this.form.imageId) {
        this.$message.warning('请上传套餐主图')
        return
      }

      if (this.form.products.length === 0) {
        this.$message.warning('请至少添加一个商品')
        return
      }

      this.submitting = true
      try {
        const productsStr = this.form.products
            .map(p => `${p.productId}:${p.productCount}`)
            .join(',')

        const descImageIds = this.descImages.length > 0
            ? this.descImages.map(img => img.id).join(',')
            : ''

        const tagString = this.form.tags.join(',')

        const data = {
          name: this.form.name,
          tag: tagString,
          price: Math.round(this.form.price * 100),
          originalPrice: Math.round(this.form.originalPrice * 100),
          stock: this.form.stock,
          voucherValidDays: this.form.voucherValidDays,
          useLimit: this.form.useLimit,
          status: this.form.status,
          description: this.form.description,
          imageId: this.form.imageId,
          descImageIds: descImageIds || null,
          products: productsStr
        }

        const res = await api.product_addCombination(data)
        const d = res.data || res

        if (d.code === '000') {
          this.$message.success('创建成功')
          this.$router.push('/Index/Product/CouponList')
        } else {
          this.$message.error(d.message || '操作失败')
        }
      } catch (e) {
        this.$message.error('操作失败，请重试')
      } finally {
        this.submitting = false
      }
    },

    handleCancel() {
      this.$router.push('/Index/Product/CouponList')
    }
  }
}
</script>

<style scoped lang="scss">
.coupon-edit-container {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}

.main-card {
  border-radius: 8px;
  overflow: hidden;
}

.card-header {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  color: #409eff;
  font-size: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding: 16px 0 12px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  &:first-child {
    padding-top: 0;
  }
}

.tag-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .tag-item {
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

.selected-tags-info {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.desc-images-upload {
  .images-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
  }

  .image-item {
    width: 120px;
    height: 120px;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
    border: 1px solid #e4e7ed;

    .image-thumb {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .image-actions {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      justify-content: center;
      gap: 20px;
      padding: 6px 0;
      opacity: 0;
      transition: opacity 0.3s;

      i {
        color: #fff;
        font-size: 16px;
        cursor: pointer;

        &:hover {
          color: #409eff;
        }
      }
    }

    &:hover .image-actions {
      opacity: 1;
    }
  }

  .upload-trigger {
    width: 120px;
    height: 120px;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #fafafa;
    transition: all 0.3s;

    &:hover {
      border-color: #409eff;
    }

    i {
      font-size: 28px;
      color: #999;
      margin-bottom: 8px;
    }

    span {
      font-size: 12px;
      color: #999;
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #999;
  }
}

.upload-row {
  display: flex;
  gap: 12px;
}

.upload-box {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: #fafafa;

  &:hover {
    border-color: #409eff;
  }

  .upload-icon {
    font-size: 24px;
    color: #999;
    margin-bottom: 4px;
  }

  span {
    font-size: 12px;
    color: #999;
  }

  .upload-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .upload-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity .3s;

    i {
      color: #fff;
      font-size: 20px;
    }

    span {
      color: #fff;
      font-size: 14px;
    }

    &:hover {
      opacity: 1;
    }
  }
}

.selected-products {
  min-height: 32px;
}

.product-select-dialog {
  .el-table {
    margin-bottom: 8px;
  }
}

.dialog-footer {
  text-align: right;
}
</style>